import { DOCUMENT } from '@angular/common';
import { DestroyRef, Injectable, inject, isDevMode } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, from, fromEvent, merge, switchMap, timer } from 'rxjs';

import { environment } from 'src/environments/environment';
import { clearNgRxPersistedLocalStorage } from '../store/ngrx-persisted-state.keys';

const MAIN_BUNDLE_RE = /\bmain[-.][A-Za-z0-9]+\.js\b/i;
const CHECK_INTERVAL_MS = 60_000;
const STABLE_CONFIRM_MS = 2_000;
/** Prevents infinite reload if the browser keeps serving the old bundle after one attempt. */
const RELOAD_TARGET_KEY = 'elevateme.version-check.reload-target';

@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private readonly doc = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly swUpdate = inject(SwUpdate, { optional: true });

  private runningBundleId: string | null = null;
  private reloadTriggered = false;

  start(): void {
    if (!environment.enableVersionCheck || this.isLocalDevServer()) {
      return;
    }

    this.runningBundleId = this.readRunningBundleId();
    if (!this.runningBundleId) {
      return;
    }

    if (!this.guardAgainstReloadLoop()) {
      return;
    }

    this.watchDeployments();
    this.watchServiceWorker();
  }

  /**
   * If we already reloaded for a target bundle but the page still runs something else,
   * stop checking (avoids a reload loop). If we landed on the target, clear and continue.
   */
  private guardAgainstReloadLoop(): boolean {
    const pendingTarget = sessionStorage.getItem(RELOAD_TARGET_KEY);
    if (!pendingTarget) {
      return true;
    }

    if (pendingTarget === this.runningBundleId) {
      sessionStorage.removeItem(RELOAD_TARGET_KEY);
      return true;
    }

    return false;
  }

  private watchDeployments(): void {
    merge(
      timer(0, CHECK_INTERVAL_MS),
      fromEvent(this.doc, 'visibilitychange').pipe(filter(() => this.doc.visibilityState === 'visible'))
    )
      .pipe(
        filter(() => this.shouldPoll()),
        switchMap(() => from(this.detectStableNewBundle())),
        filter((id): id is string => id !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((newBundleId) => this.reloadOnce(newBundleId));
  }

  private watchServiceWorker(): void {
    const sw = this.swUpdate;
    if (!sw?.isEnabled) {
      return;
    }

    sw.versionUpdates
      .pipe(
        filter((event): event is VersionReadyEvent => event.type === 'VERSION_READY'),
        filter(() => this.shouldPoll()),
        switchMap(() => from(this.detectStableNewBundle())),
        filter((id): id is string => id !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((newBundleId) => this.reloadOnce(newBundleId));

    void sw.checkForUpdate();
  }

  private async detectStableNewBundle(): Promise<string | null> {
    const first = await this.fetchDeployedBundleId();
    if (!first || first === this.runningBundleId) {
      return null;
    }

    await new Promise((r) => setTimeout(r, STABLE_CONFIRM_MS));

    const second = await this.fetchDeployedBundleId();
    if (!second || second !== first || second === this.runningBundleId) {
      return null;
    }

    return second;
  }

  private reloadOnce(newBundleId: string): void {
    if (this.reloadTriggered || !this.shouldPoll()) {
      return;
    }

    this.reloadTriggered = true;
    sessionStorage.setItem(RELOAD_TARGET_KEY, newBundleId);
    clearNgRxPersistedLocalStorage();

    const reload = () => this.doc.defaultView?.location.reload();

    if (this.swUpdate?.isEnabled) {
      void this.swUpdate.activateUpdate().finally(reload);
    } else {
      reload();
    }
  }

  private async fetchDeployedBundleId(): Promise<string | null> {
    try {
      const url = `${this.resolveIndexHtmlPath()}?ngsw-bypass=true&_=${Date.now()}`;
      const response = await fetch(url, {
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'text/html', 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
      });

      if (!response.ok) {
        return null;
      }

      return this.extractBundleId(await response.text());
    } catch {
      return null;
    }
  }

  private resolveIndexHtmlPath(): string {
    const base = this.doc.querySelector('base')?.getAttribute('href') ?? '/';
    const normalized = base.endsWith('/') ? base : `${base}/`;
    return `${normalized}index.html`.replace(/\/{2,}/g, '/');
  }

  private readRunningBundleId(): string | null {
    for (const script of this.doc.scripts) {
      if (!script.src) {
        continue;
      }
      const id = this.extractBundleId(script.src);
      if (id) {
        return id;
      }
    }
    return null;
  }

  private extractBundleId(source: string): string | null {
    return source.match(MAIN_BUNDLE_RE)?.[0].toLowerCase() ?? null;
  }

  private shouldPoll(): boolean {
    return !this.reloadTriggered && this.doc.visibilityState !== 'hidden';
  }

  private isLocalDevServer(): boolean {
    if (!isDevMode()) {
      return false;
    }
    const host = this.doc.defaultView?.location.hostname ?? '';
    return host === 'localhost' || host === '127.0.0.1';
  }
}
